// Physics-based drag-to-flip interaction for `.flip-card` elements, with a
// shadow/holographic-shine effect driven by the current rotation angle.

interface CardState {
  angleY: number;
  tiltX: number;
  velY: number;
  velX: number;
  dragging: boolean;
  startX: number;
  startY: number;
  startAngleY: number;
  startTiltX: number;
  lastX: number;
  lastY: number;
  lastTime: number;
  animating: boolean;
  flipped: boolean;
  cardW?: number;
}

const FRICTION = 0.92; // velocity decay per frame
const SENSITIVITY = 180; // degrees per card-width of drag

const cards = new Map<HTMLElement, CardState>();

function getState(card: HTMLElement): CardState {
  if (!cards.has(card)) {
    cards.set(card, {
      angleY: 0,
      tiltX: 0,
      velY: 0,
      velX: 0,
      dragging: false,
      startX: 0,
      startY: 0,
      startAngleY: 0,
      startTiltX: 0,
      lastX: 0,
      lastY: 0,
      lastTime: 0,
      animating: false,
      flipped: false,
    });
  }
  return cards.get(card)!;
}

function render(card: HTMLElement, s: CardState) {
  const inner = card.querySelector<HTMLElement>('.flip-card-inner');
  if (!inner) return;
  const shine = card.querySelector<HTMLElement>('.flip-card-shine');
  // Normalize angle for shadow calc
  const norm = ((s.angleY % 360) + 360) % 360;
  const rad = (norm * Math.PI) / 180;

  // Shadow: shifts based on rotation, gets bigger when card is angled
  const shadowX = Math.sin(rad) * 30;
  const shadowSkew = Math.abs(Math.sin(rad));
  const shadowBlur = 20 + shadowSkew * 40;
  const shadowY = 12 + shadowSkew * 15;
  const shadowAlpha = 0.25 + shadowSkew * 0.25;

  inner.style.transform = `rotateY(${s.angleY}deg) rotateX(${s.tiltX}deg)`;
  inner.style.boxShadow = `${shadowX.toFixed(1)}px ${shadowY.toFixed(0)}px ${shadowBlur.toFixed(0)}px rgba(0,0,0,${shadowAlpha.toFixed(2)})`;

  // Holographic shine — based on angle, stays visible
  if (shine) {
    const angleFactor = shadowSkew; // 0 at flat, 1 at 90deg
    const tiltFactor = Math.abs(s.tiltX) / 25;
    const targetOpacity = Math.min(1, angleFactor * 0.9 + tiltFactor * 0.4 + 0.08);
    // Smooth transition
    const prev = parseFloat(shine.style.opacity) || 0;
    const smoothed = prev + (targetOpacity - prev) * 0.25;
    const shineX = 50 + Math.sin(rad) * 60;
    const shineY = 50 + (s.tiltX / 25) * 40;
    shine.style.opacity = smoothed.toFixed(3);
    shine.style.backgroundPosition = `${shineX.toFixed(0)}% ${shineY.toFixed(0)}%`;
  }

  // Determine flipped state (front visible when ~90-270)
  const wasFront = s.flipped;
  s.flipped = norm > 85 && norm < 275;
  if (s.flipped !== wasFront) {
    card.dispatchEvent(new CustomEvent('cardflipped', { detail: { flipped: s.flipped } }));
  }
}

function startPhysics(card: HTMLElement) {
  const s = getState(card);
  if (s.animating) return;
  s.animating = true;

  function tick() {
    if (s.dragging) {
      s.animating = false;
      return;
    }

    // Apply friction
    s.velY *= FRICTION;
    s.velX *= FRICTION;

    // Stop when barely moving
    if (Math.abs(s.velY) < 0.05 && Math.abs(s.velX) < 0.05) {
      s.velY = 0;
      s.velX = 0;
      // Slowly settle tiltX to 0
      s.tiltX *= 0.85;
      if (Math.abs(s.tiltX) < 0.1) s.tiltX = 0;
      render(card, s);
      if (s.tiltX !== 0) {
        requestAnimationFrame(tick);
      } else {
        s.animating = false;
      }
      return;
    }

    s.angleY += s.velY;
    s.tiltX += s.velX;
    s.tiltX = Math.max(-25, Math.min(25, s.tiltX));
    // Dampen tilt toward 0
    s.tiltX *= 0.95;

    render(card, s);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

let anyDragging = false;

function onStart(card: HTMLElement, clientX: number, clientY: number) {
  const s = getState(card);
  s.dragging = true;
  anyDragging = true;
  s.cardW = card.offsetWidth || 300;
  s.startX = clientX;
  s.startY = clientY;
  s.startAngleY = s.angleY;
  s.startTiltX = s.tiltX;
  s.lastX = clientX;
  s.lastY = clientY;
  s.lastTime = performance.now();
  s.velY = 0;
  s.velX = 0;
  card.style.cursor = 'grabbing';
}

function onMove(card: HTMLElement, clientX: number, clientY: number) {
  const s = getState(card);
  if (!s.dragging) return;

  const now = performance.now();
  const dt = Math.max(1, now - s.lastTime);

  // Track velocity
  s.velY = ((clientX - s.lastX) / dt) * 8;
  s.velX = ((clientY - s.lastY) / dt) * -4;

  s.lastX = clientX;
  s.lastY = clientY;
  s.lastTime = now;

  const dx = clientX - s.startX;
  const dy = clientY - s.startY;
  const cardW = s.cardW!;

  s.angleY = s.startAngleY + (dx / cardW) * SENSITIVITY;
  s.tiltX = Math.max(-25, Math.min(25, s.startTiltX + (dy / cardW) * -40));

  render(card, s);
}

function onEnd(card: HTMLElement) {
  const s = getState(card);
  if (!s.dragging) return;
  s.dragging = false;
  anyDragging = false;
  card.style.cursor = 'grab';
  // Let physics take over with current velocity
  startPhysics(card);
}

function resetFlipCard(card: HTMLElement) {
  const s = getState(card);
  s.angleY = 0;
  s.tiltX = 0;
  s.velY = 0;
  s.velX = 0;
  s.flipped = false;
  s.animating = false;
  render(card, s);
}

function isCardFlipped(card: HTMLElement): boolean {
  return getState(card).flipped;
}

export function initDragFlip(): void {
  // Event delegation — mouse
  document.addEventListener('mousedown', (e) => {
    const card = (e.target as HTMLElement).closest<HTMLElement>('.flip-card');
    if (!card || card.classList.contains('local-mini-card')) return;
    e.preventDefault();
    onStart(card, e.clientX, e.clientY);
  });
  document.addEventListener('mousemove', (e) => {
    if (!anyDragging) return;
    cards.forEach((s, card) => {
      if (s.dragging) onMove(card, e.clientX, e.clientY);
    });
  });
  document.addEventListener('mouseup', () => {
    cards.forEach((s, card) => {
      if (s.dragging) onEnd(card);
    });
  });

  // Touch
  document.addEventListener(
    'touchstart',
    (e) => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('.flip-card');
      if (!card || card.classList.contains('local-mini-card')) return;
      onStart(card, e.touches[0].clientX, e.touches[0].clientY);
    },
    { passive: true },
  );
  document.addEventListener(
    'touchmove',
    (e) => {
      cards.forEach((s, card) => {
        if (s.dragging) {
          e.preventDefault();
          onMove(card, e.touches[0].clientX, e.touches[0].clientY);
        }
      });
    },
    { passive: false },
  );
  document.addEventListener('touchend', () => {
    cards.forEach((s, card) => {
      if (s.dragging) onEnd(card);
    });
  });
}

declare global {
  interface Window {
    resetFlipCard: typeof resetFlipCard;
    isCardFlipped: typeof isCardFlipped;
  }
}

// Exposed on window: the legacy inline script (index.html) calls these as bare
// globals (`resetFlipCard(...)`, `isCardFlipped(...)`).
window.resetFlipCard = resetFlipCard;
window.isCardFlipped = isCardFlipped;
